import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LangSwitch from "@/components/LangSwitch";
import Logo from "@/components/Logo";
import { ArrowLeft, Envelope, Phone, Globe, LinkedinLogo, MapPin, PencilSimple, House } from "@phosphor-icons/react";
import { pickField } from "../lib/i18nField";
import { countryLabel, serviceTagLabel } from "../lib/labels_ar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdvisorProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const dir = lang === "ar" ? "rtl" : "ltr";

  const [profile, setProfile] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("esg_token");
    axios.get(`${API}/advisors/${slug}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => { setProfile(r.data.profile); setIsOwner(!!r.data.is_owner); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#070709] text-white/40 flex items-center justify-center" data-testid="advisor-loading">Loading…</div>;
  if (notFound || !profile) return (
    <div className="min-h-screen bg-[#070709] text-white flex items-center justify-center flex-col gap-4" data-testid="advisor-not-found">
      <p className="text-white/60">{t("advisors_not_found")}</p>
      <button onClick={() => navigate("/professionals")} data-testid="advisor-back-gallery"
        className="px-4 py-2 rounded-full bg-[#01AF9B] text-white text-sm font-medium">{t("advisors_back_gallery")}</button>
    </div>
  );

  const isMine = isOwner;

  return (
    <div dir={dir} className="min-h-screen bg-[#070709] text-white" data-testid="advisor-profile">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#070709] border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2" data-testid="nav-logo-home"><Logo size="md" /></button>
          <div className="flex items-center gap-3">
            <LangSwitch />
            <button onClick={() => navigate("/professionals")} data-testid="advisor-nav-back"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white">
              <ArrowLeft size={16} /> {t("advisors_title")}
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-32 sm:pt-36 lg:pt-40 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {profile.status === "pending" && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 text-amber-200 text-sm" data-testid="advisor-pending-banner">
            {t("advisors_pending_banner")}
          </div>
        )}
        {profile.status === "rejected" && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-200 text-sm" data-testid="advisor-rejected-banner">
            <b>{t("advisors_rejected_title")}:</b> {profile.rejection_reason || "-"}
          </div>
        )}

        <div className="bg-white/[0.03] border border-white/[0.08] rounded-3xl p-8 sm:p-12">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-[#01AF9B]/30 bg-white/5 mb-6">
              {profile.image_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${profile.image_url}`}
                     alt={profile.company_name}
                     className="w-full h-full object-cover" data-testid="advisor-image" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 text-5xl">{profile.company_name?.[0]}</div>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl font-light tracking-tight mb-1" data-testid="advisor-company">{pickField(profile, "company_name", lang)}</h1>
            <p className="text-base text-white/60 mb-4" data-testid="advisor-name">{profile.first_name} {profile.last_name}</p>
            <p className="text-xl text-[#EAB308] font-light italic max-w-2xl" dir="auto" data-testid="advisor-hero">"{pickField(profile, "hero_message", lang)}"</p>
            <div className="flex items-center gap-2 text-sm text-white/60 mt-4">
              <MapPin size={14} /> <span dir="auto">{pickField(profile, "city", lang)}, {countryLabel(profile.country, lang)}</span>
            </div>
            {isMine && (
              <button onClick={() => navigate("/professionals/edit")} data-testid="advisor-edit-mine"
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
                <PencilSimple size={14} /> {t("advisors_edit_my")}
              </button>
            )}
          </div>

          <div className="border-t border-white/10 pt-8 mb-8">
            <h2 className="text-xs tracking-[0.3em] uppercase text-[#01AF9B]/80 mb-4">{t("advisors_about")}</h2>
            <p className="text-white/80 leading-relaxed whitespace-pre-wrap" dir="auto" data-testid="advisor-bio">{pickField(profile, "bio", lang)}</p>
          </div>

          {(profile.service_tags || []).length > 0 && (
            <div className="border-t border-white/10 pt-8 mb-8">
              <h2 className="text-xs tracking-[0.3em] uppercase text-[#EAB308] mb-4">{t("advisors_services")}</h2>
              <div className="flex flex-wrap gap-2" data-testid="advisor-tags">
                {profile.service_tags.map(t => (
                  <span key={t} className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-[#01AF9B]/10 border border-[#01AF9B]/30 text-[#01AF9B]">{serviceTagLabel(t, lang)}</span>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-white/10 pt-8">
            <h2 className="text-xs tracking-[0.3em] uppercase text-[#EAB308] mb-4">{t("advisors_contact")}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <a href={`mailto:${profile.email}`} data-testid="advisor-email"
                 className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors">
                <Envelope size={18} weight="fill" className="text-[#EA4335] flex-shrink-0" /> {profile.email}
              </a>
              <a href={`tel:${profile.phone}`} data-testid="advisor-phone"
                 className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors">
                <Phone size={18} weight="fill" className="text-[#25D366] flex-shrink-0" /> <bdi dir="ltr">{profile.phone}</bdi>
              </a>
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" data-testid="advisor-website"
                   className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors">
                  <Globe size={18} weight="fill" className="text-[#EAB308] flex-shrink-0" /> {profile.website.replace(/^https?:\/\//, "")}
                </a>
              )}
              {profile.linkedin && (
                <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" data-testid="advisor-linkedin"
                   className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors">
                  <LinkedinLogo size={18} weight="fill" className="text-[#0A66C2] flex-shrink-0" /> LinkedIn
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
